import { Injectable } from '@angular/core';
import { Guid, GuidTools } from '@models/guid';
import { UnitActivity } from '@models/imported/SagaSchema/UnitActivity';
import { CacheService, CacheState } from '@services/cache/cache.service';
import { CacheAction, CacheEvent } from '@services/cache/cacheEvent';
import { Observable, Subject } from 'rxjs';
import { filter, first } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class EngagedUnitsService {
  private callcardUnits: Map<Guid, UnitActivity[]> = new Map();

  private readonly _onChange = new Subject<void>();
  get $onChange(): Observable<void> {
    return this._onChange.asObservable();
  }

  constructor(private cache: CacheService) {}

  setup() {
    this.cache.state
      .pipe(
        filter(cacheState => cacheState === CacheState.ready),
        first()
      )
      .subscribe(() => {
        this.onCacheReady();
      });
  }

  private onCacheReady() {
    const engagedUnits: UnitActivity[] = this.cache
      .getListByType(UnitActivity)
      .filter(unitActivity => unitActivity.CallCardID !== GuidTools.Empty);
    engagedUnits.forEach(unitActivity => this.addUnit(unitActivity));
    this._onChange.next();

    this.cache.listenForChange(UnitActivity).subscribe((cacheEvent: CacheEvent<UnitActivity>) => this.onUnitChanged(cacheEvent));
  }

  private addUnit(unitActivity: UnitActivity) {
    this.getEngagedUnits(unitActivity.CallCardID).push(unitActivity);
  }

  private onUnitChanged(cacheEvent: CacheEvent<UnitActivity>) {
    // at reassign we are changing from one valid callcardId to another, so first remove the unitActivity if present
    const unitActivity: UnitActivity = cacheEvent.object;
    this.removeUnit(unitActivity);

    if (cacheEvent.action == CacheAction.modified) {
      if (unitActivity.CallCardID !== GuidTools.Empty) {
        this.addUnit(unitActivity);
      }
    }

    this._onChange.next();
  }

  private removeUnit(unitActivity: UnitActivity) {
    for (const engagedUnits of this.callcardUnits.values()) {
      const unitIndex = engagedUnits.indexOf(unitActivity);
      if (unitIndex > -1) {
        engagedUnits.splice(unitIndex, 1);
        break;
      }
    }
  }

  getEngagedUnits(callcardId: Guid): UnitActivity[] {
    if (!this.callcardUnits.has(callcardId)) {
      this.callcardUnits.set(callcardId, []);
    }
    return this.callcardUnits.get(callcardId);
  }
}
